/** @module pioneer-scripts */
import * as Pioneer from 'pioneer';

/**
 * Helpful functions for placing markers on a spheroid.
 * @hideconstructor
 */
export class Placemarks {
	/**
	 * Adds a placemark on an entity and returns it.
	 * @param {string} name - The name of the placemark entity.
	 * @param {string} label - The label to be displayed.
	 * @param {Pioneer.Entity} body - The entity on which the placemark will go.
	 * @param {number} latInDeg - The latitude in degrees.
	 * @param {number} lonInDeg - The longitude in degrees.
	 * @param {number} altitudeInKm - The altitude in km.
	 * @param {boolean} useOrientation - If true, orients the placemark entity to be along the east and north axes.
	 * @returns {Pioneer.Entity}
	 */
	static addPlacemark(name, label, body, latInDeg, lonInDeg, altitudeInKm, useOrientation = false) {
		const placemark = body.getScene().addEntity(name);
		placemark.setParent(body);
		placemark.setCanOcclude(false);
		placemark.setExtentsRadius(1);

		const divComponent = placemark.addComponentByClass(Pioneer.DivComponent);
		divComponent.setAlignment(new Pioneer.Vector2(0.5, 0.5));
		divComponent.getDiv().innerHTML = label;

		placemark.addController('fixed');
		placemark.addController('rotateByEntityOrientation');

		this.updateLLA(placemark, latInDeg, lonInDeg, altitudeInKm, useOrientation);

		return placemark;
	}

	/**
	 * Updates a placemark with a new location.
	 * @param {Pioneer.Entity} placemark - The placemark to change.
	 * @param {number} latInDeg - The latitude in degrees.
	 * @param {number} lonInDeg - The longitude in degrees.
	 * @param {number} altitudeInKm - The altitude in km.
	 * @param {boolean} useOrientation - If true, orients the placemark entity to be along the east and north axes.
	 */
	static updateLLA(placemark, latInDeg, lonInDeg, altitudeInKm, useOrientation = false) {
		const body = placemark.getParent();
		const fixedController = placemark.getControllerByClass(Pioneer.FixedController);
		const spheroidComponent = body.getComponentByClass(Pioneer.SpheroidComponent);
		if (fixedController === null || spheroidComponent === null) {
			throw new Error('No fixed controller and/or spheroid component.');
		}

		const lla = Pioneer.LatLonAlt.pool.get();
		const markPos = Pioneer.Vector3.pool.get();

		// Update the position using the given lat, lon, alt, and the spheroid.
		lla.set(Pioneer.MathUtils.degToRad(latInDeg), Pioneer.MathUtils.degToRad(lonInDeg), altitudeInKm);
		spheroidComponent.xyzFromLLA(markPos, lla);
		fixedController.setPosition(markPos);
		Pioneer.Vector3.pool.release(markPos);

		// If we're setting the orientation, use the east and north of the location on the spheroid.
		if (useOrientation) {
			const markOri = Pioneer.Quaternion.pool.get();
			const east = Pioneer.Vector3.pool.get();
			const north = Pioneer.Vector3.pool.get();
			spheroidComponent.eastFromLLA(east, lla);
			spheroidComponent.northFromLLA(north, lla);
			markOri.setFromAxes(east, north, undefined);
			fixedController.setOrientation(markOri);
			Pioneer.Vector3.pool.release(east);
			Pioneer.Vector3.pool.release(north);
			Pioneer.Quaternion.pool.release(markOri);
		}

		Pioneer.LatLonAlt.pool.release(lla);
	}
};
